import type { LinkState, LinkRendererContext, DiagramLinkData } from './diagram.types';
import { getItemAriaLabel } from './utils/diagramUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { itemStyles } from './themes/DiagramStyles.css';
import { ComponentChildren } from 'preact';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import { useCallback, useRef } from 'preact/hooks';
import { useResizeObserver } from '#hooks/UNSAFE_useResizeObserver';

/**
 * Props for Link
 */
export type LinkProps<K1, K2, D2, L> = {
  id: K2;
  state: LinkState;
  isDimmed?: boolean;
  activeId?: string;
  accessibleLabel?: string;
  data: D2 | DiagramLinkData<string, K1>;
  linkRenderer: (context: LinkRendererContext<K1, D2, L>) => ComponentChildren;
  supportsSelection: boolean;
  layoutOutput: L;
  onLinkSizeChanged: (width: number, height: number, id: K2) => void;
};

export const DiagramLinkElement = <K1, K2, D2 extends DiagramLinkData<K2, K1>, L>({
  id,
  state,
  activeId,
  data,
  linkRenderer,
  accessibleLabel,
  isDimmed,
  supportsSelection,
  layoutOutput,
  onLinkSizeChanged
}: LinkProps<K1, K2, D2, L>) => {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { dimmedItemStyle, linkStyles } = itemStyles;
  const context = {
    state: state,
    data: data,
    layoutOutput: layoutOutput,
    type: 'link'
  } as LinkRendererContext<K1, D2, L>;
  const ref = useRef<SVGGElement>(null);
  const optionsRef = useRef({ box: 'border-box' as const });
  useResizeObserver(
    ref,
    useCallback(
      (entry: ResizeObserverEntry) => {
        const boxSize = entry.borderBoxSize;
        const width = boxSize[0].inlineSize;
        const height = boxSize[0].blockSize;
        onLinkSizeChanged(width, height, id);
      },
      [id, onLinkSizeChanged]
    ),
    optionsRef.current
  );
  return (
    <g
      ref={ref}
      key={id}
      id={state.isFocused ? activeId : undefined}
      aria-label={getItemAriaLabel(
        translations,
        supportsSelection,
        {
          isSelected: state.isSelected
        },
        accessibleLabel
      )}
      data-oj-private-link={id}
      role="img"
      class={classNames([linkStyles, isDimmed && dimmedItemStyle])}>
      {linkRenderer(context)}
    </g>
  );
};
